import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import 'react-photo-view/dist/react-photo-view.css';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Route, Routes, matchPath, useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import {
    APP_GIT_BRANCH,
    APP_GIT_COMMIT,
    APP_URL_ACTIVITIES,
    APP_URL_ACTIVITY,
    APP_URL_CATALOG,
    APP_URL_CHAT,
    APP_URL_CHAT_HISTORY,
    APP_URL_CHAT_SHARE,
    APP_URL_CHECKOUT,
    APP_URL_HOME,
    APP_URL_HOME_CLASSIC,
    APP_URL_LIVE_READINGS,
    APP_URL_MEMORY,
    APP_URL_NOT_FOUND,
    APP_URL_PAYMENT,
    APP_URL_PAYMENT_HISTORY,
    APP_URL_PAYMENT_RESULT,
    APP_URL_PAYMENT_SUCCESS,
    APP_URL_PLANS,
    APP_URL_PROFILE,
    APP_URL_REDIRECT_CHAT,
    APP_URL_SUBSCRIPTION,
    APP_URL_USER_DELETION_STATUS,
    APP_URL_WIDGET_CHAT,
    LIVE_READINGS_ENABLED,
    STORAGE_KEY_LANDING_REDIRECT_DONE,
    STORAGE_KEY_PENDING_ADVISOR_ALIAS
} from '../const';
import useABBannerTesting from '../hooks/useABBannerTesting';
import useAuth from '../hooks/useAuth';
import useCatchRedirect from '../hooks/useCatchRedirect';
import usePayments from '../hooks/usePayments';
import { useWindowSize } from '../hooks/useWindowSize';
import useZaraEmailEntry from '../hooks/useZaraEmailEntry';
import CheckoutRisk from '../service/CheckoutRisk';
import CrazyEgg from '../service/CrazyEgg';
import ResponseService from '../service/ResponseService';
import { getAuthModalContext, getUser } from '../store/slices/authSlice';
import { fetchChats, getChatsState } from '../store/slices/chatsSlice';
import { fetchFeatures } from '../store/slices/featuresSlice';
import { checkMemoryRegion } from '../store/slices/memorySlice';
import GoogleAnalytics from '../utils/GoogleAnalytics';
import { gaEvents } from '../utils/analytics';
import { appHeight, setMetaViewportContent, setRetinaClass } from '../utils/helper';
import ProtectedRoute from './ProtectedRoute';
import SeoTags from './SeoTags';
import LiveReadingsPromoModal from './common/LiveReadingsPromoModal';
import LiveReadingsToast from './common/LiveReadingsToast';
import Spinner from './common/Spinner';
import UpdateRemoveChatModal from './common/UpdateRemoveChatModal';
import ZaraUpsellModal from './common/ZaraUpsellModal';
import ZaraWelcomeModal from './common/ZaraWelcomeModal';
import MessagePackToast from './common/chat/MessagePackOffer/MessagePackToast';
import ActivityLanding from './pages/ActivitiesLandings';
import ActivitiesPage from './pages/ActivityPage';
import AuthModal from './pages/AuthProvider/AuthModal';
import AuthToast from './pages/AuthProvider/AuthToast';
import BirthChartHost from './pages/BirthChart/BirthChartHost';
import PaymentModal from './pages/PaymentProvider/PaymentModal';

const ChatPage = lazy(() => import('./pages/ChatPage'));
const ChatHistoryPageV2 = lazy(() => import('./pages/ChatHistoryPageV2'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const AnonHomePage = lazy(() => import('./pages/AnonHomePage'));
const CatalogPageV2 = lazy(() => import('./pages/CatalogPageV2'));
const Profile = lazy(() => import('./pages/ProfileV2'));
const Page404 = lazy(() => import('./pages/Page404'));
const PageUserDeletionStatus = lazy(() => import('./pages/PageUserDeletionStatus'));
const WidgetChatRedirectPage = lazy(() => import('./pages/WidgetChatRedirectPage'));
const ChatRedirectPage = lazy(() => import('./pages/ChatRedirectPage'));
const SubscriptionRedirect = lazy(() => import('./pages/SubscriptionRedirect'));
const PaymentPage = lazy(() => import('./pages/PaymentPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const PaymentSuccessPage = lazy(() => import('./pages/PaymentSuccessPage'));
const PaymentHistoryPage = lazy(() => import('./pages/PaymentHistoryPage'));
const PaymentResultPage = lazy(() => import('./pages/PaymentResultPage'));
const MemoryPage = lazy(() => import('./pages/MemoryPage'));
const LiveReadingsPage = lazy(() => import('./pages/LiveReadingsPage'));
const PlansPage = lazy(() => import('./pages/PlansPage'));

const protectedRoutsConfig = [
    {
        path: APP_URL_CHAT_HISTORY,
        Element: ChatHistoryPageV2
    },
    {
        // HORO-10533: wildcard so the profile owns its own child routes
        // (birth-date, email, plan, …) as a master/detail pair.
        path: `${APP_URL_PROFILE}/*`,
        Element: Profile
    },
    {
        // HORO-10582: Plan Management replaced the standalone subscription page;
        // this slot now holds a redirect. Kept in the protected table where the
        // page was, since the destination is protected anyway.
        path: APP_URL_SUBSCRIPTION,
        Element: SubscriptionRedirect
    },
    {
        path: `${APP_URL_PAYMENT}`,
        Element: PaymentPage
    },
    {
        // HORO-10478: protected — there is no anonymous checkout. Every buy
        // route on the backend is `withUser` and the Checkout session needs a
        // `veda_user_id`, so anonymous users are bounced to sign-in first.
        path: `${APP_URL_CHECKOUT}`,
        Element: CheckoutPage
    },
    {
        path: `${APP_URL_PAYMENT_HISTORY}`,
        Element: PaymentHistoryPage
    },
    {
        // HORO-10479: the designed confirmation screen, replacing the generic
        // `payment-result/success` for completed purchases. Declared before the
        // `payment-result/:type` route it supersedes.
        path: `${APP_URL_PAYMENT_SUCCESS}`,
        Element: PaymentSuccessPage
    },
    {
        path: `${APP_URL_PAYMENT_RESULT}/:type`,
        Element: PaymentResultPage
    },
    {
        path: APP_URL_MEMORY,
        Element: MemoryPage
    }
];

const App = () => {
    const [_, height] = useWindowSize();
    const user = useSelector(getUser);
    const isUserAuthorized = user?.id;
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const authModalContext = useSelector(getAuthModalContext);
    const { list: chats, loading: chatsLoading } = useSelector(getChatsState);

    appHeight(height);

    useEffect(() => {
        ResponseService.init();
        GoogleAnalytics.init();
        // HORO-10370: Tier-1 super-properties + per-tab session UUID. Must
        // run after GoogleAnalytics.init() so ReactGA.gtag is wired up.
        gaEvents.initSession();
        setRetinaClass();
        setMetaViewportContent();
        CheckoutRisk.init();
        // HORO-10451 — CrazyEgg heatmaps (no-op unless REACT_APP_CRAZY_EGG_ID set).
        CrazyEgg.init();
        dispatch(checkMemoryRegion());
        // HORO-10360: public env flags, for visitors who never load GET /user.
        dispatch(fetchFeatures());

        if (APP_GIT_COMMIT && APP_GIT_BRANCH) {
            console.log(`APP Version: ${APP_GIT_BRANCH}-${APP_GIT_COMMIT}`);
        }

        return () => {
            CheckoutRisk.removeListener();
        };
    }, []);

    useAuth();
    useABBannerTesting();
    usePayments();
    useCatchRedirect();
    useZaraEmailEntry();

    // Post-login redirect (v2 only).
    //
    // `authModalContext` is set by the menu actions in MenuContent.js when
    // the auth modal opens — `'login'` (explicit Login button), `'chatHistory'`
    // (anon user tapped "Chat History"), `'memory'` (anon user tapped
    // "Memory"), or `'advisorLocked'` (anon user tried to chat with an
    // advisor outside the anonymous tier's 5 — MarketplaceActionButton,
    // CreateNewChat, useStartHomeChat, MenuContent's handleAdvisorTap) — all
    // but `login` bounced the user into sign-up first. OAuth login does a
    // full-page navigation to the provider, so an in-memory ref doesn't
    // survive the round-trip — we mirror the context into sessionStorage
    // while the modal is open and consume it from there when `user.id`
    // transitions from empty to truthy.
    //
    // Behaviour:
    //   - `chatHistory` → navigate to /chat-history (the destination they
    //     originally asked for before the auth bounce).
    //   - `memory` → navigate to /memory, same reasoning.
    //   - `advisorLocked` → navigate to /chat/:alias for the specific advisor
    //     they were blocked from (stashed separately in
    //     STORAGE_KEY_PENDING_ADVISOR_ALIAS — `vedaPostLoginContext` only
    //     ever holds the bare context string, not which advisor).
    //   - `login` + already on `/<APP_URL_CHAT>/:alias/:chatId` → stay put.
    //     useAuth's `carry-over-usage` claims the in-flight anon chat
    //     under their new account; navigating away would dump that chat.
    //   - `login` + anywhere else → navigate to their most recent chat
    //     (same selection rule the LandingPage redirect uses). If chats
    //     aren't loaded yet, dispatch `fetchChats` and pick on the next
    //     render. If they have no chats, no redirect (stay where they are).
    //   - Any other context (or no context) → no redirect.
    useEffect(() => {
        if (!authModalContext) return;
        try {
            sessionStorage.setItem('vedaPostLoginContext', authModalContext);
        } catch {
            // sandboxed sessionStorage — silently ignore, the redirect
            // just won't fire after OAuth in this edge case.
        }
    }, [authModalContext]);

    const prevUserIdRef = useRef(user?.id);
    const [pendingLoginNav, setPendingLoginNav] = useState(null);

    useEffect(() => {
        const prevId = prevUserIdRef.current;
        prevUserIdRef.current = user?.id;
        if (prevId || !user?.id) return;
        let ctx = null;
        try {
            ctx = sessionStorage.getItem('vedaPostLoginContext');
            sessionStorage.removeItem('vedaPostLoginContext');
        } catch {
            // sandboxed sessionStorage — nothing to read
        }
        if (ctx === 'login' || ctx === 'chatHistory' || ctx === 'memory' || ctx === 'advisorLocked') {
            setPendingLoginNav(ctx);
        }
    }, [user?.id]);

    // Tracks whether we've already dispatched fetchChats for the current
    // pendingLoginNav cycle. Without this guard, a brand-new user with zero
    // chats triggers an infinite loop: each fetch returns empty → chats stays
    // [] → condition `!chats?.length && !chatsLoading` stays true → re-dispatch
    // on every redux tick → /api/v1/chat?page=1 every ~30ms forever. Resets
    // when pendingLoginNav clears so a future login can re-fetch.
    const fetchAttemptedForLoginRef = useRef(false);

    useEffect(() => {
        if (!pendingLoginNav || !user?.id) {
            if (!pendingLoginNav) fetchAttemptedForLoginRef.current = false;
            return;
        }

        // HORO-10455: a real login is being resolved here (pendingLoginNav is
        // only ever set for an explicit 'login'/'chatHistory'/'memory'/
        // 'advisorLocked' context, never a session restore). Consume the
        // AnonHomePage returning-user redirect budget so a later explicit
        // logo click lands on the homepage instead of being bounced back
        // into a chat by that redirect. Idempotent.
        try {
            sessionStorage.setItem(STORAGE_KEY_LANDING_REDIRECT_DONE, '1');
        } catch {
            // sandboxed sessionStorage — the homepage redirect just may fire once.
        }

        if (pendingLoginNav === 'chatHistory') {
            setPendingLoginNav(null);
            navigate(`/${APP_URL_CHAT_HISTORY}`);
            return;
        }

        if (pendingLoginNav === 'memory') {
            setPendingLoginNav(null);
            navigate(`/${APP_URL_MEMORY}`);
            return;
        }

        if (pendingLoginNav === 'advisorLocked') {
            setPendingLoginNav(null);
            let alias = null;
            try {
                alias = sessionStorage.getItem(STORAGE_KEY_PENDING_ADVISOR_ALIAS);
                sessionStorage.removeItem(STORAGE_KEY_PENDING_ADVISOR_ALIAS);
            } catch {
                // sandboxed sessionStorage — falls through to APP_URL_CHAT below
            }
            navigate(alias ? `/${APP_URL_CHAT}/${alias}` : `/${APP_URL_CHAT}`);
            return;
        }

        // pendingLoginNav === 'login' from here on.
        // Caveat: if the user is mid-chat as anon, the carry-over flow
        // in useAuth claims that chat under their new account — stay on
        // the same URL and let them continue the conversation.
        const inActiveChat = matchPath({ path: `/${APP_URL_CHAT}/:alias/:chatId`, end: true }, location.pathname);
        if (inActiveChat) {
            setPendingLoginNav(null);
            return;
        }

        if (!chats?.length && !chatsLoading) {
            if (fetchAttemptedForLoginRef.current) {
                // Already fetched once for this login; chats list is genuinely
                // empty. Nothing to navigate to — clear pendingLoginNav so the
                // useEffect stops re-firing.
                setPendingLoginNav(null);
                return;
            }
            fetchAttemptedForLoginRef.current = true;
            dispatch(fetchChats({ reloading: true, page: 1 }));
            return;
        }
        if (chatsLoading) return;

        // Backend returns chats already sorted by `sortingOptions.lastMessageDate`
        // desc (chat-history aggregate), so the first resolvable entry IS the
        // most recent chat.
        const target = chats.find((c) => c?.advisor?.alias && c?.id);
        setPendingLoginNav(null);
        if (target) {
            navigate(`/${APP_URL_CHAT}/${target.advisor.alias}/${target.id}`);
        }
    }, [pendingLoginNav, user?.id, chats, chatsLoading, location.pathname, dispatch, navigate]);

    // Also shown behind the sign-in modal on protected routes (see ProtectedRoute).
    const homeElement = <AnonHomePage />;

    return (
        <>
            <SeoTags />
            <Suspense
                fallback={
                    <div className="overlay">
                        <Spinner />
                    </div>
                }
            >
                <main className="page">
                    <Routes>
                        {/*
                            HORO-10426: AnonHomePage owns `/`. The legacy promo
                            LandingPage stays reachable at /classic (non-crawlable)
                            for reference / fallback.
                        */}
                        <Route index path={APP_URL_HOME} element={homeElement} exact />
                        <Route path={APP_URL_HOME_CLASSIC} element={<LandingPage />} />
                        <Route path={`${APP_URL_REDIRECT_CHAT}/:chatId`} element={<ChatRedirectPage useNewChatUrl={true} />} />
                        {/*
                            The `key` on each ChatPage route is load-bearing: all three
                            render the same component, so without distinct keys React
                            reconciles them in place and the page does NOT remount when
                            the landing mints a chat and navigates to it. ChatPage's
                            tarot state machine relies on that remount — see the note at
                            its `v2_tarot_handled_` lookup. Do not remove.
                        */}
                        <Route path={`${APP_URL_CHAT}/:alias/:chatId`} element={<ChatPage key="chat" />} />
                        {/*
                            HORO-10563: CatalogPageV2 replaced the filterable v1 catalog
                            outright (no split test) — it drops category filters entirely, so
                            /catalog/:category has nowhere to go and redirects to the bare grid
                            rather than 404ing old filtered/bookmarked links.
                        */}
                        <Route path={APP_URL_CATALOG} element={<CatalogPageV2 />} />
                        <Route path={`${APP_URL_CATALOG}/:category`} element={<Navigate to={`/${APP_URL_CATALOG}`} replace />} />
                        {/*
                            HORO-10471: deliberately a PUBLIC route, not a
                            protected one — the waitlist is a demand test and
                            must be reachable by anonymous users, who are a
                            large part of the signal we're measuring.
                        */}
                        {LIVE_READINGS_ENABLED && <Route path={APP_URL_LIVE_READINGS} element={<LiveReadingsPage />} />}
                        {/*
                            HORO-10703: public on purpose. The whole point is
                            that a logged-out visitor can read what Premium
                            costs before deciding to have an account at all —
                            the page handles the register-then-checkout bounce
                            itself, since /checkout is protected.
                        */}
                        <Route path={APP_URL_PLANS} element={<PlansPage />} />
                        <Route path={`${APP_URL_ACTIVITIES}`} element={<ActivitiesPage />} />
                        <Route path={`${APP_URL_ACTIVITIES}/:category`} element={<ActivitiesPage />} />
                        <Route path={`${APP_URL_ACTIVITY}/:activityId`} element={<ActivityLanding />} />
                        {/* HORO-10585: shared links open in the chat page read-only. */}
                        <Route path={`${APP_URL_CHAT_SHARE}/:alias/:chatId`} element={<ChatPage key="share" />} />
                        <Route
                            path={`${APP_URL_CHAT_SHARE}/:chatId`}
                            element={<ChatRedirectPage wasShared={true} useNewChatUrl={true} />}
                        />
                        <Route path={APP_URL_REDIRECT_CHAT} element={<ChatRedirectPage />} />
                        <Route path={`${APP_URL_WIDGET_CHAT}/:alias`} element={<WidgetChatRedirectPage />} />
                        <Route path={`/${APP_URL_NOT_FOUND}`} element={<Page404 />} />
                        <Route path={`/${APP_URL_USER_DELETION_STATUS}`} element={<PageUserDeletionStatus />} />

                        {protectedRoutsConfig.map((rout) => {
                            const { path, Element } = rout;
                            return (
                                <Route
                                    key={path.toString()}
                                    path={path}
                                    element={
                                        <ProtectedRoute key={path.toString()} user={!!isUserAuthorized} fallback={homeElement}>
                                            <Element />
                                        </ProtectedRoute>
                                    }
                                    exact
                                />
                            );
                        })}
                        <Route path={`${APP_URL_CHAT}/:alias`} element={<ChatPage key="landing" />} />
                        <Route path="*" element={<Page404 />} />
                    </Routes>
                </main>
            </Suspense>
            <AuthModal />
            <AuthToast />
            {/* HORO-10360: birth chart flow + chart view; self-gates on the feature flag. */}
            <BirthChartHost />
            <PaymentModal />
            <ZaraUpsellModal />
            {isUserAuthorized && <ZaraWelcomeModal />}
            <UpdateRemoveChatModal />
            {/*
                HORO-10471: both self-gate — the modal renders only for a
                signed-in user on a chat route who hasn't dismissed or joined,
                the toast only after a join made from the modal.
            */}
            <LiveReadingsPromoModal />
            <LiveReadingsToast />
            <MessagePackToast />
            <ToastContainer />
        </>
    );
};

export default App;
